/*
 * INDEX.JSX - Add Task Component with Global User Permissions
 *
 *
 * SUMMARY:
 * Task creation and editing component with enhanced permission controls allowing
 * global users to maintain edit capabilities during restricted project states like
 * quote processing. Handles tasks, variations, and options with comprehensive validation.
 *
 */
import React, { Fragment, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Table from "react-bootstrap/Table";
import "../scss/task.scss";
import "../scss/variation.scss";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileUploader from "../../../components/file-uploader";
import { useNavigate } from "react-router";
import { PROJECT_MANAGEMENT } from "../../../constants/route-paths";
import idx from "idx";
import { taskFields } from "../../../constants/fields.constant";
import DatePickerInput from "../../../components/inputs/date-picker";
import {
  addTaskAction,
  updateProfitMarginAction,
  updateSubTaskAction,
} from "../../../slice/add-task";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import ProfitMarginLock from "../../../components/profitMarginLock-dialog";
import {
  addOption,
  addStage,
  addSubTask,
  addTask,
  getOption,
  getTask,
  updateOption,
  updateSubTask,
  updateTask,
  deleteSubTask as deleteSubTaskAction,
} from "../../../actions/project-management.action";
import SubTask from "./subtask";
import {
  toPercent,
  toUSD,
  numberFormat,
} from "../../../util/currency-formatter";
import { roundValue } from "src/util/decimal-utils";
import { errorSnackBar, warningSnackBar } from "../../../actions/common.action";
import Cookies from "js-cookie";
import { subTaskCalculation } from "../../../util/calculations/calculate-sub-task";
import { formatDecimalTime } from "../../../util/common";
import CustomInput, { LockInput } from "../../../components/inputs/new-input";
import AllocateResource from "../../timesheet/allocate-resource-dialog";
import ConfirmAllocateResource from "../../timesheet/confirm-allocate-resource-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import moment from "moment";
import {
  allocateResource,
  allocateSchedule,
  deleteResource,
  editTimesheet,
  getResourceAllocations,
} from "../../../actions/timesheet.action";
import { getUserList } from "../../../actions/user-management.action";
import SaveTemplate from "../../../components/save-template";
import ListItemTemplate from "../../../components/list-template/item";
import { materialToForm } from "./binders/material";
import { subcontractorToForm, updateSubcontractorSubtask } from "./binders/subcontractor";
import { laborToForm } from "./binders/labor";
import {
  getLaborList,
  getUnits,
  listCategories,
} from "../../../actions/inventory.action";
import { addResourcesAction } from "../../../slice/resources";
import { fileValidator } from "../../../util/validators/file";
import { getSubcontractorsData } from "../utils/get-subcontractors";
import TaskTemplate from "./taskTemplate";
import { getTaskTemplates } from "../../../actions/template-management.action";
import { updateLaborSubtask } from "./binders/labor";
import { updateMaterialSubTask } from "./binders/material";
import { v4 as uuidv4 } from "uuid";
import { getInheritedFieldOpsExclusion } from "../utils/field-ops-inheritance";

/**
 * Recursively find the path of a stage (section)
 * @param {Array} stages All stages/sections hierarchy
 * @param {String} targetId The ID of the stage to find
 * @param {Array} path Current path accumulation
 * @returns {Array|null} Array of stage names or null if not found
 */


export default function AddTask({
  updatePermission,
  open,
  dialogClose,
  isSubTask,
  isOption,
  isVariation,
  taskId,
  optionId,
  profitMargin,
  taskTemplateData,
  ...props
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const projectDetail = useSelector((state) => state.projectDetail) || {};
  const projectSummary = useSelector((state) => state.projectSummary) || {};
  const entitlements = useSelector((state) => state.user.entitlements);
  const user = useSelector((state) => state.user);
  const task = useSelector((state) => state.task);
  const resources = useSelector((state) => state.resources);
  const [editMode, setEditMode] = useState(
    !(props.editOption || props.editSubTask || props.editTask) || false
  );
  const [fields, setFields] = useState(JSON.parse(JSON.stringify(taskFields)));
  const [templateQty, setTemplateQty] = useState(
    taskTemplateData?.selectedQty || ""
  );
  const subTaskArray =
    useSelector((state) => state.task.task.subTaskArray) || [];

  const [taskState, setTaskState] = useState({
    showlockIcon: true,
    profitMarginDialogState: false,
    showItemTemplateDialog: false,
    saveTemplateDialog: false,
    saveTemplateData: {
      type: "",
      data: {},
    },
  });
  const [deleteSubTask, setDeleteSubTask] = useState({
    open: false,
    id: [],
    type: "Item",
    index: "",
    description: "",
  });

  const [allocationState, setAllocationState] = useState({
    allocates: [],
    allocationId: null,
    showlockIcon: true,
    profitMarginDialogState: false,
    allocationDate: null,
    editTimesheet: false,
    allocationType: "",
    userId: user.userData.global_user ? "" : user.userData.id,
    usersList: [],
  });
  const [units, setUnits] = useState([]);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [excludeFromFieldOps, setExcludeFromFieldOps] = useState(false);
  const categoryList = useSelector((state) => state.catalog.catalogs) || [];
  const bottomElementRef = useRef(null);
  const [isBottomVisible, setIsBottomVisible] = useState(true);
  const currentStageId = idx(task, (_) => _.task.stageId) || props.stageId;
  const isFieldOpsInheritedExcluded = getInheritedFieldOpsExclusion(
    idx(projectSummary, (_) => _.sectionData) || [],
    currentStageId,
  );
  const effectiveExcludeFromFieldOps =
    isFieldOpsInheritedExcluded || excludeFromFieldOps;

  useEffect(() => {
    if (categoryList.length === 0)
      dispatch(listCategories()).catch((e) => console.error(e));
    if (_.isEmpty(idx(projectSummary, (_) => _.subcontractors)))
      getSubcontractorsData({ type: "sub-contractor", dispatch });
    initLoad();
    getViewport();
  }, []);

  // Reload task/option data when modal opens with a new taskId/optionId for editing
  // This ensures the modal fetches fresh data when opened for a different task
  useEffect(() => {
    if (!open) return; // Only run when modal is open

    const { editTask, editSubTask, editOption } = props;

    if (taskId && (editTask || editSubTask)) {
      // Fetch task data when modal opens in edit mode
      dispatch(getTask(taskId)).then((data) => {
        preFill(data);
        setTaskState((prev) => ({
          ...prev,
          showlockIcon: data.overrideProfitMargin == true ? false : true,
        }));
      }).catch((e) => {
        console.error('Error fetching task:', e);
      });
    } else if (optionId && editOption) {
      // Fetch option/variation data when modal opens in edit mode
      dispatch(getOption(optionId)).then((data) => {
        preFill(data);
        setTaskState((prev) => ({
          ...prev,
          showlockIcon: data.overrideProfitMargin == true ? false : true,
        }));
        props.updateSelectedSection &&
          props.updateSelectedSection(data.stageId);
      }).catch((e) => {
        console.error('Error fetching option:', e);
      });
    }
  }, [open, taskId, optionId, props.editTask, props.editSubTask, props.editOption]);

  const getViewport = () => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsBottomVisible(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    if (bottomElementRef.current) observer.observe(bottomElementRef.current);

    return () => {
      if (bottomElementRef.current)
        observer.unobserve(bottomElementRef.current);
    };
  };

  const handleCloseClick = () => {
    if (editMode && isVariation) {
      setShowCloseConfirmDialog(true);
    } else {
      dialogClose();
    }
  };

  const handleCloseConfirm = () => {
    setShowCloseConfirmDialog(false);
    dialogClose();
  };

  const handleCloseCancel = () => {
    setShowCloseConfirmDialog(false);
  };

  const initLoad = async () => {
    setExcludeFromFieldOps(false);

    if (!resources.list.length) _getLaborList();
    const { editTask, editSubTask, editOption } = props;

    const stages = idx(projectSummary, (_) => _.project.stages) || [];
    const sectionItems = getFlatStages(stages);

    setFields((prev) => ({
      ...prev,
      section: {
        ...prev.section,
        items: sectionItems,
        value: props.stageId || "",
      },
      profitMargin: {
        ...prev.profitMargin,
        value: profitMargin,
      },
    }));

    dispatch(
      updateProfitMarginAction({
        profitMargin: profitMargin,
        profitMarginType: "PERCENTAGE",
      })
    );

    dispatch(getUnits({ sortBy: "name" }))
      .then((res) => {
        setUnits(res);
      })
      .catch((e) => console.error(e));

    const isEditingTask = taskId && (editTask || editSubTask);
    const isEditingOption = optionId && editOption;

    if (!isEditingTask && !isEditingOption) {
      dispatch(addTaskAction({}));
    }
    if (taskTemplateData) {
      let { subTasks = [], name = "" } = taskTemplateData;
      setFields((prev) => ({
        ...prev,
        taskName: {
          ...prev.taskName,
          value: name,
        },
      }));
      setTemplateQty(taskTemplateData.selectedQty);
      loadItemFromTemplate(subTasks, true, { profitMargin: profitMargin });
    }
    await _getUsersList();
    getAllocationList();
  };

  const _getLaborList = () => {
    dispatch(getLaborList())
      .then((data) => {
        let labors = (data || []).map((labor, i) => {
          labor.sno = i + 1;
          return labor;
        });
        dispatch(addResourcesAction(labors));
      })
      .catch((e) => console.error(e));
  };

  const _getUsersList = async () => {
    const status = "ACTIVE";
    const list = await dispatch(getUserList({ status }));
    const usersList =
      (Array.isArray(list) &&
        list.map(({ id, firstName, lastName }) => ({
          key:
            (firstName === null ? "" : firstName) +
            " " +
            (lastName === null ? "" : lastName),
          value: id,
        }))) ||
      [];
    setAllocationState((prev) => ({
      ...prev,
      usersList,
    }));
  };

  const preFill = (task) => {
    dispatch(
      addTaskAction(
        task,
        idx(projectDetail, (_) => _.subcontractors) ||
        idx(projectSummary, (_) => _.subcontractors) ||
        []
      )
    );
    dispatch(
      updateProfitMarginAction({
        profitMargin: parseFloat(task.profitMargin || profitMargin || 0),
        profitMarginType: task.profitMarginType,
      })
    );
    setExcludeFromFieldOps(!!task.excludeFromFieldOps);
    setFields((prev) => ({
      ...prev,
      taskName: {
        ...prev.taskName,
        value: task.name,
      },
      description: {
        ...prev.description,
        value: task.description || "",
      },
      date: {
        ...prev.date,
        value: new Date(task.date),
      },
      user: {
        ...prev.user,
        value: task.user || "",
      },
      section: {
        ...prev.section,
        value: task.stageId || props.stageId || "",
      },
      is_actual_materialcost: {
        ...prev.is_actual_materialcost,
        value: task.isActualMaterialcost || false,
      },
      profitMarginType: {
        ...prev.profitMarginType,
        value: task.profitMarginType,
      },
      profitMargin: {
        ...prev.profitMargin,
        value: parseFloat(task.profitMargin || profitMargin || 0),
      },
      attachments: {
        ...prev.attachments,
        attachmentArr: (task.attachment || []).map((attach) => {
          if (_.isString(attach)) {
            return JSON.parse(attach);
          } else {
            return attach;
          }
        }),
      },
    }));
  };

  const generateTaskPO = () => {
    const { projectId, subTaskId } = props;
    navigate(`/${PROJECT_MANAGEMENT}/purchase-order/${projectId}/create-purchase-order?taskId=${isSubTask ? subTaskId : taskId}`
    );
  };

  const getFlatStages = (stages, result = [], depth = 0) => {
    stages.forEach((stage) => {
      const prefix = "\u00A0\u00A0".repeat(depth);
      result.push({ key: prefix + stage.name, value: stage.id });
      if (stage.stages && stage.stages.length > 0) {
        getFlatStages(stage.stages, result, depth + 1);
      }
    });
    return result;
  };





  const getModalHeader = () => {
    const { editTask, editSubTask, editOption, action } = props;


    let modalHeading = "Create Task";
    if (!editMode) {
      modalHeading = isSubTask
        ? "SubTask Details"
        : isOption && props.projectType !== "PROJECT"
          ? "Option Details"
          : isVariation
            ? "Variation Details"
            : "Task Details";
    } else {
      if (isSubTask) modalHeading = "Create SubTask";

      if (isOption) {
        modalHeading = "Create Option";
        if (action === "revise") modalHeading = "Revise Option";
        if (isVariation) modalHeading = "Create Variation";
      }
      if (editTask) modalHeading = "Edit Task";
      if (editSubTask) modalHeading = "Edit SubTask";
      if (editOption) {
        modalHeading = "Edit Option";
        if (action === "revise") modalHeading = "Revise Option";
      }
      if (isVariation && editOption) modalHeading = "Edit Variation";
    }
    return modalHeading;
  };

  const inputChangeHandler = (e, fieldName = "", index = "") => {
    const subTaskArray = idx(task, (_) => _.task.subTaskArray) || [];
    let _temp = _.cloneDeep(subTaskArray);

    let description;
    let descriptionLength;
    if (e.target.value) {
      description = e?.target?.value.trim();
      descriptionLength = description.length;
    }

    if (_temp[index]) {
      _temp[index].descriptionError = !description;
      _temp[index].descriptionLength = description && descriptionLength < 2;
    } else {
      _temp[index] = {
        descriptionError: !description,
        descriptionLength: description && descriptionLength < 2,
      };
    }

    if (e.target.type === "file") {
      const fileToUpload = e.target.files;
      Array.isArray(fileToUpload) &&
        fileToUpload.forEach((file) => {
          const fileValidation = fileValidator({ file });
          const { validFileSize, validFileType, error } = fileValidation;
          if (!validFileSize.valid && !error)
            dispatch(errorSnackBar(validFileSize.error));
          else if (!validFileType.valid && !error)
            dispatch(errorSnackBar(validFileType.error));
          else if (error) dispatch(errorSnackBar(error.error));
          else fields[e.target.name].value.push(file);
        });
    } else {
      if (e.target.name === "profitMarginType") {
        fields["profitMargin"].value = "";
      }
      fields[e.target.name].value = e.target.value;
    }

    fields[e.target.name].touched = true;
    fields[e.target.name].error = false;
    fields[e.target.name].valid = true;
    setFields((prev) => ({
      ...prev,
      ...fields,
    }));
    recalcualteProfitMargin(e.target.name);
  };

  const recalcualteProfitMargin = (name) => {
    if (name === "profitMarginType" || name === "profitMargin") {
      updateProfitChanges();
    }
  };

  const getRoundedProfitMarginDisplay = (value) => {
    if (value === "" || value === null || value === undefined) {
      return value;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? roundValue(numericValue, 2) : value;
  };

  const updateProfitChanges = () => {
    dispatch(
      updateProfitMarginAction({
        profitMargin: fields.profitMargin.value,
        profitMarginType: fields.profitMarginType.value,
      })
    );
  };

  const lockIconChange = () => {
    const { showlockIcon } = taskState;
    const formData = new FormData();
    formData.set("overrideProfitMargin", showlockIcon);
    if (taskId) dispatch(updateTask(formData, taskId)).then((res) => { });

    setTaskState((prev) => ({
      ...prev,
      showlockIcon: !prev.showlockIcon,
    }));

    if (!showlockIcon) fields.profitMargin.value = props.projectMarginValue;

    updateProfitChanges();
    setFields((prev) => ({
      ...prev,
      ...fields,
    }));
    profitMarginDialogHandler();
  };

  const profitMarginDialogHandler = () => {
    setTaskState((prev) => ({
      ...prev,
      profitMarginDialogState: !prev.profitMarginDialogState,
    }));
  };

  const handleAddTask = () => {
    const subTaskArray = idx(task, (_) => _.task.subTaskArray) || [];
    let _temp = _.cloneDeep(subTaskArray);
    const clonedAccordionFields = {
      mapId: uuidv4(), // mapId is a client-side UUID used as a stable key for subtasks that do not yet have a persisted backend id
      labor: [],
      subcontractor: [],
      material: [],
      taskName: "",
      categoryId: "",
      is_actual_materialcost: false,
      descriptionError: true,
      descriptionLength: true,
    };
    _temp.push(clonedAccordionFields);
    dispatch(updateSubTaskAction(_temp));
  };

  const getFiles = () => {
    const files = (fields.attachments.value || []).map((doc, i) => (
      <Fragment key={i}>
        <span> {doc.name}</span>
        <span onClick={() => removeFile(i, "new")}>
          <DeleteIcon />
        </span>
      </Fragment>
    ));
    const attachments = (fields.attachments.attachmentArr || []).map(
      (doc, i) => (
        <Fragment key={i}>
          <span> {doc.name}</span>
          <span onClick={() => removeFile(i, "old")}>
            <DeleteIcon />
          </span>
        </Fragment>
      )
    );
    if (!files.length && !attachments.length)
      files.push(<span key={"space"}>&nbsp;</span>);
    return (
      <>
        {files}
        {attachments}
      </>
    );
  };
  const removeFile = (index, type) => {
    if (type === "new") {
      const data = fields.attachments.value.filter((doc, i) => i !== index);
      fields.attachments.value = data;
    }
    if (type === "old") {
      const data = fields.attachments.attachmentArr.filter(
        (doc, i) => i !== index
      );
      fields.attachments.attachmentArr = data;
    }
    setFields((prev) => ({
      ...prev,
      ...fields,
    }));
  };

  const submitHandler = async (type = "") => {
    const { projectId, projectType, editOption, action } = props;
    let stageId = fields.section.value || props.stageId;
    const subTaskArray = idx(task, (_) => _.task.subTaskArray) || [];

    // Strip mapId before sending to the backend: it is a client-only UUID used
    // for React keys and local lookup, and should not be persisted as part of
    // the subtask payload. The real persisted identifier is subtask.id.

    let _temp = _.cloneDeep(subTaskArray).map(({ mapId, ...cleanSubtask }) => {
      // keep backend id, remove only the UI key
      return cleanSubtask;
    });

    const allDescriptionsError = _temp.every(
      (subtask) => subtask.descriptionError === false
    );
    if (_temp.length > 0 && !allDescriptionsError) {
      dispatch(errorSnackBar("A description is needed for all Items"));
      return;
    }
    let _taskId = taskId;
    const formData = new FormData();
    formData.set("name", fields.taskName.value);
    formData.set("profitMargin", fields.profitMargin.value || 0);
    formData.set("marginType", fields.profitMarginType.value);
    formData.set("date", fields.date.value);
    if (isFieldOpsInheritedExcluded && !excludeFromFieldOps) {
      dispatch(
        warningSnackBar(
          "This item is excluded from field ops via its parent section and cannot be included here.",
        ),
      );
      setExcludeFromFieldOps(true);
    }
    formData.set(
      "excludeFromFieldOps",
      isFieldOpsInheritedExcluded ? true : excludeFromFieldOps,
    );
    if (templateQty) {
      formData.set("templateQuantity", templateQty);
    }

    if (
      Array.isArray(fields.attachments.attachmentArr) &&
      Array.isArray(fields.attachments.value)
    ) {
      for (let x = 0; x < fields.attachments.value.length; x++) {
        formData.append("attachment", fields.attachments.value[x]);
      }
    }

    if (!isOption && !isVariation) {
      if (_taskId) {
        dispatch(updateTask(formData, _taskId))
          .catch((e) => console.error(e));
        (idx(deleteSubTask, (_) => _.id) || []).map((deleteId) => {
          dispatch(deleteSubTaskAction(deleteId))
            .then((res) => { })
            .catch((e) => console.error(e));
        });
      } else {
        if (stageId === "") {
          const formDatanew = new FormData();
          formDatanew.set("name", "DummyStage");
          formDatanew.set("isDummyStage", true);
          await dispatch(addStage(formDatanew, projectId, true))
            .then((res) => {
              stageId = res.id;
              dispatch(addTask(formData, stageId)).then((res) => {
                _taskId = res.id;
              });
            })
            .catch((e) => console.error(e));
        } else {
          await dispatch(addTask(formData, stageId))
            .then((res) => {
              _taskId = res.id;
            })
            .catch((e) => console.error(e));
        }
      }
    } else if (isOption || isVariation) {
      if (optionId) {
        dispatch(updateTask(formData, optionId))
          .catch((e) => console.error(e));
        (idx(deleteSubTask, (_) => _.id) || []).map((deleteId) => {
          dispatch(deleteSubTaskAction(deleteId))
            .then((res) => { })
            .catch((e) => console.error(e));
        });
      }
      formData.set("project_id", projectId || Cookies.get("projectId"));
      stageId && formData.set("stageId", stageId);
      if (isVariation) formData.set("isVariation", true);
      if (isOption && projectType === "TENDER") formData.set("isOption", true);

      if (!editOption || (editOption && action === "revise")) {
        if (optionId && action === "revise") {
          formData.set("isRevised", true);
          formData.set("optionId", optionId);
        }
        await dispatch(
          addOption(
            formData,
            isVariation === false ? "isOption" : "isVariation"
          )
        )
          .then((res) => {
            // if (this.props.getStages) this.props.getStages();
            // this.props.getOptions();
            // if (this.props.getVariations) this.props.getVariations();
            _taskId = res.id;
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        await dispatch(
          updateOption(
            formData,
            optionId,
            isVariation === false ? "isOption" : "isVariation"
          )
        )
          .then((res) => {
            // if (this.props.getStages) this.props.getStages();
            // this.props.getOptions();
            // if (this.props.getVariations) this.props.getVariations();
            _taskId = res.id;
          })
          .catch((e) => console.error(e));
      }
    }

    if (type === "EntireTask" && _taskId) {
      let index = 1;
      for await (let subtask of _temp) {
        if (action === "revise")
          await saveSubTask(subtask, _taskId, "addsubtask", index++);
        else
          await saveSubTask(
            subtask,
            subtask.id ? subtask.id : _taskId,
            subtask.id ? "updateSubTask" : "addsubtask",
            index++
          );
      }

      dialogClose();
      if (props.getStages) props.getStages();
      if (props.getOptions) props.getOptions();
      if (props.getVariations) props.getVariations();
      if (props.handleConvertOptiontoTaskClose)
        props.handleConvertOptiontoTaskClose();
    }
  };

  const formatNewMaterials = (materialsarray) => {
    const materialData = materialsarray.map((item) => ({
      item: item.name.value,
      id: item.id,
      categoryId: item.categoryId,
      manufacturer: item.manufacturer,
      partNo: item.partNo,
      cost: item.costPerItem.value,
      saleAmount: item.saleAmount.value,
      saleAmountLock: item.saleAmountLock.value,
      qty: item.qty.value,
      unit: item.unit.value || null,
      total: item.total.value,
      isInlineItem: item.isInlineItem || false,
    }));

    return materialData;
  };

  const filterLabor = (id) => {
    let laborsList = idx(projectSummary, (_) => _.labors) || [];
    let labors = laborsList.find((labor) => labor.id === id);
    let labor = {};
    let allLabors = idx(resources, (_) => _.list) || [];
    labor = allLabors.find((labor) => labor.id === id);
    if (!labor) return {};
    labor = _.cloneDeep(labor);
    labor["calloutCost"] = 0;
    labor["averageCost"] = idx(labors, (_) => _.averageCost) || labor.cost;
    labor["skillDescription"] = "";
    labor["cost"] = idx(labors, (_) => _.cost) || 0;
    return labor || {};
  };

  const formatLaborData = (laborarray) => {
    const laborData = laborarray.map((_labor) => {
      let _laborItem = filterLabor(_labor.id.value);
      return {
        name: _labor.name.value,
        level: _labor.level.value,
        id: _labor.id.value,
        cost: _labor.costPerItem.value,
        qty: _labor.qty.value,
        calloutCost: _labor.calloutCost.value || 0,
        averageCost: (_labor.labourCost.value !== undefined && _labor.labourCost.value !== null && _labor.labourCost.value !== "")
          ? _labor.labourCost.value
          : (_laborItem ? _laborItem.averageCost : 0),
        is_personnel: _labor.is_personnel?.value ?? (_laborItem.is_personnel ?? true),
        total: _labor.total.value,
      };
    });
    return laborData;
  };
  const formatSubcontractorData = (subcontractorarray) => {
    const subcontractorData = subcontractorarray.map((_subcontractor) => ({
      name: _subcontractor.name.value,
      id: _subcontractor.id.value,
      Cost: _subcontractor.Cost.value,
      saleAmount: _subcontractor.saleAmount.value,
      saleAmountLock: _subcontractor.saleAmountLock.value,
      description: _subcontractor.description.value,
    }));
    return subcontractorData;
  };

  const handleAllocationDialog = () => {
    setAllocationState((prev) => ({
      ...prev,
      showAllocationDialog: true,
      allocationDate: null,
      allocationId: null,
      editTimesheet: false,
    }));
  };

  const _allocateResource = async (data) => {
    let { editTimesheet } = allocationState;
    let start = new Date(data.startTime);
    let end = new Date(data.endTime);
    let startTime = new Date(data.startDate);
    let endTime = new Date(data.endDate);
    startTime.setHours(start.getHours(), start.getMinutes(), 0, 0);
    endTime.setHours(end.getHours(), end.getMinutes(), 0, 0);
    startTime = moment.utc(startTime).format("YYYY-MM-DDTHH:mmZ").toString();
    endTime = moment.utc(endTime).format("YYYY-MM-DDTHH:mmZ").toString();
    data["startTime"] = startTime;
    data["endTime"] = endTime;
    delete data.startDate;
    delete data.endDate;
    if (editTimesheet) data.allocationType = "WORK";
    await handleAllocation(data, "WORK");
  };

  const handleAllocation = async (data, type) => {
    try {
      const { editTimesheet, allocationId } = allocationState;
      let { confirmDialogState, confirmData } = allocationState;
      let allocationsList = [];
      if (editTimesheet || allocationId) {
        allocationsList = await _editTimesheet(data);
      } else {
        allocationsList = await dispatch(allocateResource(data));
      }

      confirmDialogState = true;
      confirmData = allocationsList;

      setAllocationState((prev) => ({
        ...prev,
        showAllocationDialog: false,
        confirmData,
        confirmDialogState,
      }));
    } catch (e) {
      setAllocationState((prev) => ({
        ...prev,
        showAllocationDialog: true,
      }));
    }
  };

  const _allocateSchedule = async (data) => {
    try {
      await dispatch(allocateSchedule(data));
      // if (data.allocationType === "WORK") await dispatch(getResourceAllocations());
      // if (data.previousType === "WORK" && data.allocationType !== "WORK")
      //   await dispatch(getResourceAllocations());
      getAllocationList();
      setAllocationState((prev) => ({
        ...prev,
        confirmDialogState: false,
        showAllocationDialog: false,
        taskDialogState: false,
      }));
      if (props.getStages) props.getStages();
      if (dialogClose) dialogClose();
    } catch (e) {
      console.error(e);
      setAllocationState((prev) => ({
        ...prev,
        confirmDialogState: false,
        showAllocationDialog: false,
        taskDialogState: false,
      }));
      getAllocationList();
      // setAllocationState(prev => ({
      //   ...prev,
      //   confirmDialogState: true,
      // }));
    }
  };

  const _editTimesheet = async (data) => {
    const { allocationId } = allocationState;
    data.previousType = "WORK";
    data.allocationResourceId = allocationId;
    data.startTime = moment
      .utc(data.startTime)
      .format("YYYY-MM-DDTHH:mmZ")
      .toString();
    data.endTime = moment
      .utc(data.endTime)
      .format("YYYY-MM-DDTHH:mmZ")
      .toString();
    return await dispatch(editTimesheet(allocationId, data));
  };

  const deleteHandler = async (allocationId) => {
    await dispatch(deleteResource("WORK", allocationId));
    deleteDialogHandler();
    dialogClose();
  };

  const deleteDialogHandler = (event = {}) => {
    const eventStatus = idx(event, (_) => _.event.status) || "";
    const isDelete = eventStatus
      ? eventStatus !== "PENDING"
        ? false
        : true
      : true;
    if (isDelete) {
      setAllocationState((prev) => ({
        ...prev,
        deleteDialogState: !prev.deleteDialogState,
        deleteEvent: event,
        showAllocationDialog: false,
        allocationId: event.id,
      }));
    } else {
      dispatch(warningSnackBar(`This allocation is already ${eventStatus}`));
    }
  };
  const mapUserName = (userId) => {
    let { usersList } = allocationState;
    let name = "";
    usersList.map((user) => {
      if (user.value === userId) {
        name = user.key;
        return user.key;
      }
    });
    return name;
  };
  const getAllocationList = () => {
    const allocates = [];
    dispatch(
      getResourceAllocations({
        taskId: taskId || optionId,
        projectId: props.projectId,
      })
    ).then((res) => {
      const all = JSON.parse(JSON.stringify(res));
      all.forEach((allocate, i) => {
        // Prefer the work-hour engine's per-period breakdown (Standard vs Overtime,
        // no OT1/OT2 tiers) — the legacy overtime_1/overtime_2 columns are never
        // populated by the engine and only apply to entries that predate it.
        const timePeriods = allocate.work_hour_breakdown?.time_periods;
        let normalHours, overtimeHours;
        if (Array.isArray(timePeriods) && timePeriods.length > 0) {
          normalHours = timePeriods
            .filter((p) => p.time_type === "STANDARD")
            .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
          overtimeHours = timePeriods
            .filter((p) => p.time_type !== "STANDARD")
            .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
        } else {
          overtimeHours = (allocate.overtime_1 || 0) + (allocate.overtime_2 || 0);
          normalHours =
            moment(allocate.endTime).diff(
              moment(allocate.startTime),
              "hours",
              true
            ) - overtimeHours;
        }
        allocates.push({
          id: allocate.id,
          userId: allocate.userId,
          user: mapUserName(allocate.userId),
          date:
            moment.utc(allocate.startTime).format("DD/MM/YYYY") +
            " - " +
            moment.utc(allocate.endTime).format("DD/MM/YYYY"),
          starttime: moment.utc(allocate.startTime).format("hh:mm a"),
          endtime: moment.utc(allocate.endTime).format("hh:mm a"),
          normalHours,
          overtimeHours,
          status: allocate.status,
        });
      });

      setAllocationState((prev) => ({
        ...prev,
        allocates,
      }));
      const columns = [
        { title: "Assigned User", field: "user" },
        { title: "Date", field: "date" },
        { title: "Start Time", field: "starttime" },
        { title: "End Time", field: "endtime" },
        {
          title: "Normal Hours",
          field: "normalHours",
          render: (e) => e?.normalHours ? `${numberFormat(e.normalHours, true)} (${formatDecimalTime(e.normalHours)})` : '0.00 (0H)'
        },
        {
          title: "Overtime",
          field: "overtimeHours",
          render: (e) => e?.overtimeHours ? `${numberFormat(e.overtimeHours, true)} (${formatDecimalTime(e.overtimeHours)})` : '0.00 (0H)'
        },
        { title: "Status", field: "status" },
      ];
      setAllocationState((prev) => ({
        ...prev,
        columns: columns,
      }));
    });
  };
  const handleEditTimesheet = (allocationId) => {
    const allocation = allocationState.allocates.filter(
      (_) => _.id === allocationId
    );
    setAllocationState((prev) => ({
      ...prev,
      allocationType: "WORK",
      allocationId: allocationId,
      userId: allocation.userId,
      allocationResourceId: allocationId,
      allocationDate: new Date(allocation.starttime),
      showAllocationDialog: true,
      editTimesheet: true,
    }));
  };

  const saveSubTask = async (subtask, id = "", type = "", order = 1) => {
    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
        }
        return value;
      };
    };
    // if (!idx(subtask, _ => _.isedited)) return;

    const formData = new FormData();
    const material = formatNewMaterials(subtask["material"]);
    const laborData = formatLaborData(subtask["labor"]);
    const subcontractorData = formatSubcontractorData(subtask["subcontractor"]);

    let calculation = subTaskCalculation({
      subtask: subtask,
      profitMargin: fields.profitMargin.value,
      profitMarginType: fields.profitMarginType.value,
      isForm: true,
    });

    formData.set("description", subtask.description);
    formData.set("categoryId", subtask.categoryId || "");
    formData.set("order", order);
    formData.set("is_actual_materialcost", subtask.is_actual_materialcost);

    formData.set("labor_total_cost", calculation.labor.cost || 0);
    formData.set("laborTotal_SaleAmount", calculation.labor.sale || 0);
    formData.set("labor_profit", calculation.labor.profit || 0);

    formData.set(
      "materialTotal_Cost",
      parseFloat(calculation.material.cost || 0)
    );
    formData.set(
      "materialTotal_SaleAmount",
      parseFloat(calculation.material.sale || 0)
    );
    formData.set(
      "material_Profit",
      parseFloat(calculation.material.profit || 0)
    );

    formData.set(
      "subcontractorTotal_Cost",
      calculation.subcontractor.cost || 0
    );
    formData.set(
      "subcontractorTotal_SaleAmount",
      calculation.subcontractor.sale || 0
    );
    formData.set("subcontractor_Profit", calculation.subcontractor.profit || 0);

    formData.set("profitMargin", fields.profitMargin.value || 0);
    formData.set("marginType", fields.profitMarginType.value);
    formData.set("profitMarginCost", calculation.total_profit || 0);
    formData.set("totalProfit", calculation.total_profit || 0);
    formData.set("templateId", subtask.templateId || "");
    formData.set("templateName", subtask.templateName || "");
    formData.set("templateQuantity", subtask.templateQuantity || "");

    for (let i = 0; i < material.length; i++) {
      formData.append("material[]", JSON.stringify(material[i]));
    }

    for (let i = 0; i < laborData.length; i++) {
      formData.append("labor[]", JSON.stringify(laborData[i]));
    }
    for (let i = 0; i < subcontractorData.length; i++) {
      formData.append(
        "subcontractor[]",
        JSON.stringify(subcontractorData[i], getCircularReplacer())
      );
    }

    if (material.length === 0) {
      formData.append("material[]", null);
    }
    if (laborData.length === 0) {
      formData.append("labor[]", null);
    }
    if (subcontractorData.length === 0) {
      formData.append("subcontractor[]", null);
    }
    if (type === "addsubtask") {
      await dispatch(addSubTask(formData, id)).then((res) => {
        // if (type != "EntireTask") {
        //   // this.dialogClose();
        //   if (this.props.getStages) this.props.getStages();
        // }
        // this.props.getOptions();
        // if (lastIndex === 1) {
        //   this.dialogClose();
        // }
      });
    } else if (type == "updateSubTask") {
      await dispatch(updateSubTask(formData, id)).then((res) => {
        // if (type != "EntireTask") {
        //   // this.dialogClose();
        //   if (this.props.getStages) this.props.getStages();
        // }
        // this.props.getOptions();
        // if (lastIndex === 1) {
        //   this.dialogClose();
        // }
      });
    }
  };

  const handleSaveTemplateDialog = (type = "", data = {}) => {
    setTaskState((prev) => ({
      ...prev,
      saveTemplateDialog: !prev.saveTemplateDialog,
      saveTemplateData: {
        type: type,
        data: data,
      },
    }));
  };

  const handleItemTemplateDialog = () => {
    setTaskState((prev) => ({
      ...prev,
      showItemTemplateDialog: !prev.showItemTemplateDialog,
    }));
  };

  const loadItemFromTemplate = (items = [], skipClose = false, data = {}) => {
    let { profitMargin } = data;
    if (!profitMargin) profitMargin = fields.profitMargin.value;
    const subTaskArray = idx(task, (_) => _.task.subTaskArray) || [];
    let _temp = _.cloneDeep(subTaskArray);
    // Separate new and existing items
    const { newItems, existingItems } = items.reduce(
      (acc, item) => {
        const exists = _temp.some(
          (existingTask) => existingTask.templateId === item.id
        );

        if (exists) {
          acc.existingItems.push(item.description);
        } else {
          acc.newItems.push(item);
        }
        return acc;
      },
      { newItems: [], existingItems: [] }
    );

    // If there are existing items, show warning
    if (existingItems.length > 0) {
      dispatch(
        warningSnackBar(
          `The following items already exist: ${existingItems.join(", ")}`
        )
      );
    }

    // If no new items to add, just close dialog
    if (newItems.length === 0) {
      if (!skipClose) handleItemTemplateDialog();
      return;
    }

    for (const iterator of newItems) {
      const {
        material = [],
        labor = [],
        subcontractor = [],
        description,
        selectedQty,
        id,
      } = iterator;

      const cloneSubTask = {
        mapId: uuidv4(), // mapId is a client-side UUID used as a stable key for subtasks that do not yet have a persisted backend id
        templateId: id,
        description: description,
        categoryId: iterator.categoryId || "",
        selectedQty: selectedQty,
        templateName: description,
        templateQuantity: selectedQty,
        labor: labor
          .map((_labor) =>
            laborToForm(_labor, { qty: iterator.selectedQty, filterLabor })
          )
          .filter((_labor) => !_.isEmpty(_labor)),
        subcontractor: subcontractor.map((_subcontractor) =>
          subcontractorToForm(_subcontractor, {
            qty: iterator.selectedQty,
            profitMargin: profitMargin,
            profitMarginType: fields.profitMarginType.value,
          })
        ),
        material: material.map((_material) =>
          materialToForm(_material, {
            qty: iterator.selectedQty,
            profitMargin: profitMargin,
            profitMarginType: fields.profitMarginType.value,
          })
        ),
        is_actual_materialcost: false,
        descriptionError: !description ? true : false,
        descriptionLength: !description ? true : false,
        isedited: true,
      };
      _temp.push(cloneSubTask);
    }
    dispatch(updateSubTaskAction(_temp));
    if (!skipClose) handleItemTemplateDialog();
  };

  const deleteSubTaskHandler = ({
    subtaskIndex = "",
    subTaskId = [],
    type = "Item",
    currentId = "",
    description = "",
  }) => {
    setDeleteSubTask((prev) => ({
      ...prev,
      open: !prev.open,
      id: subTaskId,
      type: type,
      index: subtaskIndex,
      currentId: currentId,
      description: description,
    }));
  };
  const getDeleteSubTask = () => {
    return deleteSubTask;
  };

  const refreshHandler = async (items) => {
    if (!items.length) return;
    const { task, selectedQty } = items[0];
    const templateId = task.templateId;
    setTemplateQty(selectedQty);
    let template;
    try {
      template = await dispatch(getTaskTemplates(templateId));
    } catch (err) {
      console.error("Failed to load template", templateId, err);
      return;
    }
    if (!template || !Array.isArray(template.subTasks)) {
      dispatch(warningSnackBar("Unable to load the linked template. Refresh aborted."));
      return;
    }

    const updated = subTaskArray.map((currentSubtask, i) => {
      const matchingTemplateSubtask = template.subTasks.find(
        (templateSubtask) =>
          (templateSubtask.id || "") === currentSubtask.templateId
      );
      if (!matchingTemplateSubtask) {
        return currentSubtask;
      }
      const templateMaterials = Array.isArray(matchingTemplateSubtask.material)
        ? matchingTemplateSubtask.material.map((m) =>
          typeof m === "string" ? JSON.parse(m) : m
        )
        : [];
      const templateLabors = Array.isArray(matchingTemplateSubtask.labor)
        ? matchingTemplateSubtask.labor.map((l) =>
          typeof l === "string" ? JSON.parse(l) : l
        )
        : [];
      const templateSubcontractors = Array.isArray(matchingTemplateSubtask.subcontractor)
        ? matchingTemplateSubtask.subcontractor.map((s) =>
          typeof s === "string" ? JSON.parse(s) : s
        )
        : [];

      const updatedMaterials = updateMaterialSubTask(
        currentSubtask,
        templateMaterials,
        { templateQuantity: selectedQty }
      );
      const updatedLabours = updateLaborSubtask(
        currentSubtask,
        templateLabors,
        { templateQuantity: selectedQty }
      );
      const updatedSubcontractors = updateSubcontractorSubtask(
        currentSubtask,
        templateSubcontractors,
        { templateQuantity: selectedQty },
        {
          profitMargin: fields.profitMargin?.value,
          profitMarginType: fields.profitMarginType?.value,
        }
      );

      return {
        ...currentSubtask,
        material: updatedMaterials,
        labor: updatedLabours,
        subcontractor: updatedSubcontractors,
        templateQuantity: selectedQty,
      };
    });

    dispatch(updateSubTaskAction(updated));
  };

  // Determine if the current user is a global user
  const isGlobalUser = idx(user, (_) => _.userData.global_user);

  // Determine if a quote is in progress
  const isQuoteInProgress =
    idx(projectSummary, (_) => _.project.quoteType) === "quote" &&
    idx(projectSummary, (_) => _.project.res.status) === "IN_PROGRESS";

  // Determine if an option's proposal status is PENDING for a variation
  const isVariationOptionPending =
    isVariation &&
    idx(
      projectSummary,
      (_) =>
        _.optionData.find((option) => option.id === optionId)?.proposal_status
    ) === "PENDING";

  // Determine if a variation is being revised
  const isVariationBeingRevised = isVariation && props.action === "revise";
  // Determine if a variation is being new
  const isNewVariation = isVariation && props.action === "new";
  // Determine if a variation is being edited
  const isVariationBeingEdited = isVariation && props.action === "edit";
  // Determine if the edit button should be enabled
  const enableEditBtn = () => {
    if (isQuoteInProgress && !isVariation) {
      return (
        (isGlobalUser || updatePermission) &&
        isQuoteInProgress &&
        (isGlobalUser || updatePermission)
      );
    } else if (isVariation) {
      // Allow global users/super admins to edit approved variations
      const isApprovedVariation =
        isVariation &&
        idx(
          projectSummary,
          (_) =>
            _.optionData.find((option) => option.id === optionId)
              ?.proposal_status
        ) === "APPROVED";
      return (
        isVariationOptionPending ||
        isVariationBeingRevised ||
        isNewVariation ||
        isVariationBeingEdited ||
        (isApprovedVariation && (isGlobalUser || updatePermission))
      );
    } else {
      return true;
    }
  };

  const editBtnDataCy = isSubTask
    ? "edit-subTask"
    : isOption && props.projectType !== "PROJECT"
      ? "edit-option"
      : isVariation
        ? "edit-variation"
        : "edit-task";
  const saveBtnDataCy = isSubTask
    ? "save-subTask"
    : isOption && props.projectType !== "PROJECT"
      ? "save-option"
      : isVariation
        ? "save-variation"
        : "save-task";
  const cancelBtnDataCy = isSubTask
    ? "cancel-subTask"
    : isOption && props.projectType !== "PROJECT"
      ? "cancel-option"
      : isVariation
        ? "cancel-variation"
        : "cancel-task";

  const activeStageId = fields.section.value || props.stageId || idx(task, (_) => _.task.stageId);
  const stageHierarchy =
    idx(projectSummary, (_) => _.sections[activeStageId].stageHierarchy) || "";
  const breadcrumbDisplay = stageHierarchy.replace(/ > /g, " / ");

  return (
    <>
      {allocationState.deleteDialogState && (
        <DeleteDialog
          open={allocationState.deleteDialogState}
          id={allocationState.allocationId}
          deleteHandler={() => deleteHandler(allocationState.allocationId)}
          dialogClose={deleteDialogHandler}
          moduleName="Allocation"
          description="Allocation"
        />
      )}
      {!allocationState.deleteDialogState &&
        allocationState.showAllocationDialog && (
          <AllocateResource
            open={allocationState.showAllocationDialog}
            dialogClose={() => {
              setAllocationState((prev) => ({
                ...prev,
                showAllocationDialog: false,
              }));
            }}
            usersList={allocationState.usersList}
            allocateResource={_allocateResource}
            allocationId={allocationState.allocationId}
            allocationType={allocationState.allocationType}
            moduleName={"TaskDetails"}
            editTimesheet={allocationState.editTimesheet}
            allocationDate={allocationState.allocationDate}
            taskId={taskId}
            projectId={props.projectId}
            userId={allocationState.userId}
          />
        )}
      {allocationState.confirmDialogState && (
        <ConfirmAllocateResource
          open={allocationState.confirmDialogState}
          data={allocationState.confirmData}
          getAllocations={getAllocationList}
          dialogClose={() => {
            setAllocationState((prev) => ({
              ...prev,
              confirmDialogState: false,
            }));
          }}
          allocationType={allocationState.allocationType}
          allocateSchedule={_allocateSchedule}
        />
      )}
      <Modal
        show={open}
        onHide={() => handleCloseClick()}
        dialogClassName="modal-90w create-modal"
        aria-labelledby="example-custom-modal-styling-title"
        data-cy={"task-modal"}
      >
        <Modal.Header closeButton data-cy={"task-modal-header"}>
          <Modal.Title id="example-custom-modal-styling-title">
            {getModalHeader()}
            {breadcrumbDisplay && (
              <span className="breadcrumb-nav">{breadcrumbDisplay}</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className={`modal-body-container variation-modal`}
          >
            <ProfitMarginLock
              open={taskState.profitMarginDialogState}
              lockIconChange={() => lockIconChange()}
              dialogClose={profitMarginDialogHandler}
              moduleName="Task"
              modalHeading={"Profit Margin Lock Changes"}
              text={"profit margin"}
              data-cy="profit-margin-lock-changes"
              message={
                taskState.showlockIcon
                  ? "Are you sure you want to change the Project default profit margin? - Any items using default profit margins will also be updated to this value"
                  : ""
              }
            />
            <SaveTemplate
              open={taskState.saveTemplateDialog}
              dialogClose={() => handleSaveTemplateDialog()}
              data={taskState.saveTemplateData.data}
              type={taskState.saveTemplateData.type}
            />
            {taskState.showItemTemplateDialog && (
              <ListItemTemplate
                open={taskState.showItemTemplateDialog}
                dialogClose={() => handleItemTemplateDialog()}
                addHandler={loadItemFromTemplate}
                hideMaterialIssues={true}
              />
            )}
            <div className="row">
              <div className="col-lg-4 col-md-12 col-sm-12 mt-3">
                <CustomInput
                  placeholder="Enter task name"
                  label={
                    isSubTask
                      ? "SubTask Name"
                      : isOption
                        ? "Option Name"
                        : isVariation
                          ? "Variation Name"
                          : "Task Name"
                  }
                  {...fields.taskName}
                  className={isVariation ? "variation-description" : ""}
                  disabled={!editMode}
                  onChange={(e) => inputChangeHandler(e)}
                  data-cy={
                    isVariation ? "variation-name-input" : "task-name-input"
                  }
                />
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <DatePickerInput
                  disabled={!editMode}
                  {...fields.date}
                  label={"Date"}
                  data-cy={isVariation ? "variation-date" : "task-date"}
                  onChange={(e) =>
                    inputChangeHandler(
                      { target: { value: e, name: fields.date.name } },
                      "date"
                    )
                  }
                />
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12 mt-3">
                <LockInput
                  lock={
                    <span className="lock-icon">
                      {taskState.showlockIcon ? (
                        <LockIcon
                          onClick={() => {
                            if (editMode) profitMarginDialogHandler();
                          }}
                        />
                      ) : (
                        <LockOpenIcon
                          onClick={() => {
                            if (editMode) profitMarginDialogHandler();
                          }}
                        />
                      )}
                    </span>
                  }
                  placeholder="Enter profit margin"
                  {...fields.profitMargin}
                  value={getRoundedProfitMarginDisplay(
                    fields.profitMargin.value
                  )}
                  label={"Profit Margin %"}
                  data-cy="profit-margin"
                  disabled={!editMode || taskState.showlockIcon}
                  onChange={(e) => {
                    if (taskState.showlockIcon) return;
                    if (e.target.value < 0) return;
                    if (
                      fields &&
                      fields.profitMarginType &&
                      fields.profitMarginType.value === "PERCENTAGE" &&
                      e.target.value > 100
                    )
                      return;
                    inputChangeHandler(e);
                  }}
                />
              </div>
              <div
                className={`col-lg-4 col-md-6 col-sm-12 mt-3 fileupload-section`}
              >
                <div className="file-section-part position-relative h-auto px-0">
                  <div className="file-part">
                    <FileUploader
                      fileHandler={(files) => {
                        if (!editMode) return;
                        inputChangeHandler({
                          target: {
                            files,
                            type: "file",
                            name: "attachments",
                          },
                        });
                      }}
                    />
                    <label htmlFor="#file-section"></label>
                  </div>
                  <input
                    type="file"
                    id="file-section"
                    data-cy="file-upload"
                  ></input>
                </div>
              </div>
              <div
                className={`col-lg-4 col-md-6 col-sm-12 mt-3`}
              >
                <div className="form-group">
                  <div className="input-block">
                    <label className="input-label">Uploaded Documents</label>
                    <p className="m-0 doc-info">{getFiles()}</p>
                  </div>
                </div>
              </div>
              {!isSubTask && (
                <div className="col-lg-4 col-md-6 col-sm-12 mt-3 d-flex align-items-center">
                  <div className="form-check m-0">
                    <input
                      id="exclude-from-field-ops"
                      type="checkbox"
                      className="form-check-input"
                      checked={effectiveExcludeFromFieldOps}
                      disabled={!editMode || isFieldOpsInheritedExcluded}
                      onChange={(e) => setExcludeFromFieldOps(e.target.checked)}
                      data-cy="exclude-from-field-ops-checkbox"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exclude-from-field-ops"
                    >
                      Exclude from field ops
                    </label>
                    {isFieldOpsInheritedExcluded && (
                      <small className="text-muted d-block mt-1">
                        Inherited from parent section
                      </small>
                    )}
                  </div>
                </div>
              )}
              {idx(task, (_) => _.task.templateId) &&
                idx(task, (_) => _.task.templateQuantity) > 0 && (
                  <>
                    <div
                      className={`col-lg-4 col-md-6 col-sm-12 mt-3 task-selector align-self-center pt-0`}
                    >
                      <TaskTemplate
                        templateId={idx(task, (_) => _.task.templateId) || ""}
                        templateName={
                          idx(task, (_) => _.task.templateName) || ""
                        }
                        selectedQty={
                          idx(task, (_) => _.task.templateQuantity) ||
                          templateQty
                        }
                        item={task}
                        editMode={editMode}
                        enableEditBtn={enableEditBtn}
                        refreshHandler={refreshHandler}
                        isTaskTemplateQty={true}
                      />
                    </div>
                    <div className="col-lg-8 col-md-6 col-sm-12 mt-3"></div>
                  </>
                )}
              {isVariation &&
                props.renderMultiSelectJSX &&
                props.renderMultiSelectJSX()}
            </div>
            {!isBottomVisible && (
              <div className="position-absolute d-flex gap-2 end-0">
                {editMode && enableEditBtn() && (
                  <>
                    <Button
                      type="submit"
                      className="ms-auto task-save-btn secondarybtn"
                      data-cy={saveBtnDataCy}
                      onClick={() => submitHandler("EntireTask")}
                    >
                      SAVE
                    </Button>
                    <Button
                      type="submit"
                      className="secondarybtn task-cancel-btn cancelbtn"
                      data-cy={cancelBtnDataCy}
                      onClick={dialogClose}
                    >
                      CANCEL
                    </Button>
                  </>
                )}
                {!editMode && enableEditBtn() && (
                  <Button
                    type="submit"
                    className="ms-auto me-3 secondarybtn"
                    data-cy={editBtnDataCy}
                    onClick={() => setEditMode(true)}
                  >
                    {isSubTask
                      ? "Edit SubTask"
                      : isOption && props.projectType !== "PROJECT"
                        ? "Edit Option"
                        : isVariation
                          ? "Edit Variation"
                          : "Edit Task"}
                  </Button>
                )}
              </div>
            )}

            <Tabs
              defaultActiveKey="items"
              id="uncontrolled-tab-example"
              className="mb-2"
              data-cy="task-tabs"
            >
              <Tab eventKey="items" title="Items" data-cy="items-tab">
                <SubTask
                  subcontractors={
                    idx(projectDetail, (_) => _.subcontractors) ||
                    idx(projectSummary, (_) => _.subcontractors) ||
                    []
                  }
                  profitMargin={fields.profitMargin.value}
                  profitMarginType={fields.profitMarginType.value}
                  subTaskArray={idx(task, (_) => _.task.subTaskArray) || []}
                  isDisabled={!editMode}
                  handleSaveTemplateDialog={handleSaveTemplateDialog}
                  wTemplatePermission={props.wTemplatePermission}
                  deleteSubTaskHandler={deleteSubTaskHandler}
                  getDeleteSubTask={getDeleteSubTask}
                  categoryList={categoryList}
                  unitList={units}
                  laborsList={props.labors}
                  isVariation={isVariation}
                  editMode={editMode}
                  enableEditBtn={enableEditBtn}
                />

                {editMode && (
                  <div className="col-12 justify-content-end gap-2 d-flex btn-res">
                    <Button
                      onClick={handleItemTemplateDialog}
                      className="primarybtn add-template-btn"
                    >
                      ADD TEMPLATE
                    </Button>
                    <Button
                      onClick={handleAddTask}
                      className="primarybtn add-item-btn"
                    >
                      ADD ITEM
                    </Button>
                  </div>
                )}
                <div
                  className={`col-12 variation-table-container mt-2`}
                  data-cy="total-table"
                >
                  <div className="row g-0">
                    <div className="col-lg-4 col-md-12"></div>
                    {/** Modal Bottom Area */}
                    <div className="col-lg-7 col-md-11 col-sm-11 Variation-summary-table">
                      <Table
                        bordered
                        className={`table-create mb-0 tbl-fixed tbl-fixed-width variation-table variation-table-subtask`}
                        data-cy="task-table"
                      >
                        <tbody>
                          <tr>
                            <th className="variation-label-width"></th>
                            <th className="text-right" data-cy="material">
                              <strong>Material</strong>
                            </th>
                            <th className="text-right" data-cy="contractor">
                              <strong>Contractor</strong>
                            </th>
                            <th className="text-right" data-cy="resource">
                              <strong>Resource</strong>
                            </th>
                            <th className="text-right" data-cy="planned-effort">
                              <strong>Planned Effort</strong>
                            </th>
                            <th data-cy="total">
                              <strong>Total</strong>
                            </th>
                          </tr>
                          <>
                            <tr>
                              <td
                                className="variation-label-width fw-bold"
                                data-cy="cost"
                              >
                                <strong>Cost</strong>
                              </td>
                              <td>
                                <span
                                  className="tableinfo"
                                  data-cy="cost-material"
                                >
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.material.cost
                                    )
                                  )}
                                </span>
                              </td>
                              <td className="text-right">
                                <span
                                  className="tableinfo"
                                  data-cy="cost-contractor"
                                >
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.subcontractor.cost
                                    )
                                  )}
                                </span>
                              </td>
                              <td className="text-right">
                                <span
                                  className="tableinfo"
                                  data-cy="cost-resource"
                                >
                                  {toUSD(
                                    idx(task, (_) => _.task.total.labor.cost)
                                  )}
                                </span>
                              </td>
                              <td className="text-right">
                                <span
                                  className="tableinfo"
                                  data-cy="planned-effort-total"
                                >
                                  {idx(task, (_) => _.task.total.forecastHours) || 0} hrs
                                </span>
                              </td>
                              <td>
                                <span
                                  className="tableinfo"
                                  data-cy="cost-total"
                                >
                                  {toUSD(
                                    idx(task, (_) => _.task.total.subtotal)
                                  )}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="variation-label-width fw-bold"
                                data-cy="profit"
                              >
                                <strong> Profit</strong>
                              </td>
                              <td className="text-right">
                                <div
                                  className="text-wrap profit-font-style"
                                  profit-font-style
                                  data-cy="profit-material"
                                >
                                  (
                                  {toPercent(
                                    idx(
                                      task,
                                      (_) => _.task.total.material.profit
                                    ),
                                    idx(
                                      task,
                                      (_) => _.task.total.material.cost
                                    )
                                  )}
                                  ){" "}
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.material.profit
                                    )
                                  )}
                                </div>
                              </td>
                              <td className="text-right">
                                <div
                                  className="text-wrap profit-font-style"
                                  data-cy="profit-contractor"
                                >
                                  (
                                  {toPercent(
                                    idx(
                                      task,
                                      (_) => _.task.total.subcontractor.profit
                                    ),
                                    idx(
                                      task,
                                      (_) => _.task.total.subcontractor.cost
                                    )
                                  )}
                                  ){" "}
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.subcontractor.profit
                                    )
                                  )}
                                </div>
                              </td>
                              <td className="text-right">
                                <div
                                  className="text-wrap profit-font-style"
                                  data-cy="profit-resource"
                                >
                                  (
                                  {toPercent(
                                    idx(
                                      task,
                                      (_) => _.task.total.labor.profit
                                    ),
                                    idx(task, (_) => _.task.total.labor.cost)
                                  )}
                                  ){" "}
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.labor.profit
                                    )
                                  )}
                                </div>
                              </td>
                              <td></td>
                              <td className="text-right">
                                <div
                                  className="text-wrap profit-font-style"
                                  data-cy="profit-total"
                                >
                                  (
                                  {toPercent(
                                    idx(
                                      task,
                                      (_) => _.task.total.total_profit
                                    ),
                                    idx(task, (_) => _.task.total.subtotal)
                                  )}
                                  ){" "}
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.total_profit
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td
                                className="variation-label-width fw-bold sale-label"
                                data-cy="sale"
                              >
                                Sale
                              </td>
                              <td className="text-right">
                                <strong
                                  className="tableinfo"
                                  data-cy="sale-material"
                                >
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.material.sale
                                    )
                                  )}
                                </strong>
                              </td>
                              <td className="text-right">
                                <strong
                                  className="tableinfo"
                                  data-cy="sale-contractor"
                                >
                                  {toUSD(
                                    idx(
                                      task,
                                      (_) => _.task.total.subcontractor.sale
                                    )
                                  )}
                                </strong>
                              </td>
                              <td className="text-right">
                                <strong
                                  className="tableinfo"
                                  data-cy="sale-resource"
                                >
                                  {toUSD(
                                    idx(task, (_) => _.task.total.labor.sale)
                                  )}
                                </strong>
                              </td>
                              <td></td>
                              <td className="text-right">
                                <strong
                                  className="tableinfo"
                                  data-cy="sale-total"
                                >
                                  {toUSD(
                                    idx(task, (_) => _.task.total.total)
                                  )}
                                </strong>
                              </td>
                            </tr>
                          </>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </Tab>
              {/** Timesheets Tab */}
              {entitlements.scheduleMenu && props.projectType !== "TENDER" && (
                <Tab eventKey="user" title="Timesheets">
                  <>
                    <div className="list-title">
                      <h6>Allocated Users</h6>
                      <div className="table-responsive">
                        <Table
                          bordered
                          className="table-create table-material tbl-fixed"
                        >
                          <thead>
                            <tr>
                              <th>Assigned User</th>
                              <th>Date</th>
                              <th>Start Time</th>
                              <th>End Time</th>
                              <th>Normal Hours</th>
                              <th>Overtime</th>
                              <th>Status</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allocationState.allocates.map((e, i) => {
                              return (
                                <tr key={i}>
                                  <td>{mapUserName(e.userId)}</td>
                                  <td>{e.date}</td>
                                  <td>{e.starttime}</td>
                                  <td>{e.endtime}</td>
                                  <td>{numberFormat(e.normalHours, true)} ({formatDecimalTime(e.normalHours)})</td>
                                  <td>{numberFormat(e.overtimeHours, true)} ({formatDecimalTime(e.overtimeHours)})</td>
                                  <td className="status">{e.status}</td>
                                  <td>
                                    {editMode && (
                                      <div className="d-flex justify-content-center">
                                        <div className="action-edit me-2">
                                          <EditIcon
                                            onClick={() =>
                                              handleEditTimesheet(e.id)
                                            }
                                          />
                                        </div>
                                        <div className="action-edit me-2">
                                          <DeleteIcon
                                            onClick={() =>
                                              deleteDialogHandler(e)
                                            }
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            {allocationState.allocates.length < 1 && (
                              <tr>
                                <td className="text-center" colSpan={8}>
                                  No Data
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>

                    {taskId !== "" && editMode && (
                      <div className="col-12 mt-2 d-flex btn-res">
                        <Button
                          type="submit"
                          className="ms-auto primarybtn"
                          onClick={handleAllocationDialog}
                        >
                          + Assign User
                        </Button>
                      </div>
                    )}
                  </>
                </Tab>
              )}
            </Tabs>

            <div className="col-12 d-flex mt-3 gap-2" ref={bottomElementRef}>
              {props.wTemplatePermission && (
                <Button
                  onClick={() =>
                    handleSaveTemplateDialog(
                      "task",
                      idx(task, (_) => _.task)
                    )
                  }
                  className="primarybtn"
                >
                  SAVE AS TASK TEMPLATE
                </Button>
              )}

              {props.wSupplierPermission &&
                idx(projectSummary, (_) => _.project.projectType) !==
                "TENDER" &&
                !isOption &&
                !editMode && (
                  <Button
                    type="submit"
                    className="primarybtn"
                    onClick={generateTaskPO}
                  >
                    GENERATE PO
                  </Button>
                )}

              {/* Render Save and Cancel buttons when editMode, and enableEditBtn are true */}
              {editMode && enableEditBtn() && (
                <>
                  <Button
                    type="submit"
                    className="ms-auto task-save-btn secondarybtn"
                    data-cy={saveBtnDataCy}
                    onClick={() => submitHandler("EntireTask")}
                  >
                    SAVE
                  </Button>
                  <Button
                    type="submit"
                    className="secondarybtn task-cancel-btn cancelbtn"
                    data-cy={cancelBtnDataCy}
                    onClick={handleCloseClick}
                  >
                    CANCEL
                  </Button>
                </>
              )}
              {/* Render Edit button when editMode is false, and enableEditBtn is true */}
              {!editMode && enableEditBtn() && (
                <Button
                  type="submit"
                  className="ms-auto me-3 secondarybtn"
                  data-cy={editBtnDataCy}
                  onClick={() => setEditMode(true)}
                >
                  {isSubTask
                    ? "Edit SubTask"
                    : isOption && props.projectType !== "PROJECT"
                      ? "Edit Option"
                      : isVariation
                        ? "Edit Variation"
                        : "Edit Task"}
                </Button>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Close Confirmation Dialog */}
      <Modal
        show={showCloseConfirmDialog}
        onHide={handleCloseCancel}
        dialogClassName="modal-md"
        aria-labelledby="close-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="close-confirmation-modal">Confirm Close</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to close this variation? Any unsaved changes
            will be lost.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleCloseConfirm}>
            Close Without Saving
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
